import { create } from 'zustand';

import type { PressureState } from '../types/simulationPressure';
import type {
  Simulation,
  SimulationAgentState,
  SimulationAgentStateSnapshot,
  SimulationIntervention,
  SimulationMessage,
  SimulationPlan,
  SimulationStatus,
  SimulationTickSnapshot,
  SimulationWorkspaceArtifact,
  SimulationWorkspaceRevision,
} from '../types/simulation';

export type WorkspaceTab = 'documents' | 'plans' | 'artifacts';
export type DirectorView = 'workspace' | 'agents' | 'timeline' | 'analytics';

interface SimulationStoreState {
  simulations: Simulation[];
  activeSimulationId: string | null;
  activeSimulation: Simulation | null;
  pressureState: PressureState | null;
  messages: SimulationMessage[];
  workspaceArtifacts: SimulationWorkspaceArtifact[];
  revisionsByArtifactId: Record<string, SimulationWorkspaceRevision[]>;
  interventions: SimulationIntervention[];
  agents: SimulationAgentState[];
  ticks: SimulationTickSnapshot[];
  selectedArtifactId: string | null;
  selectedAgentId: string | null;
  activeWorkspaceTab: WorkspaceTab;
  activeDirectorView: DirectorView;
  isDirectorDockExpanded: boolean;
  viewingTick: number | null;
  isBusy: boolean;
  error: string | null;
  setSimulations: (simulations: Simulation[]) => void;
  setActiveSimulationId: (simulationId: string | null) => void;
  setActiveSimulation: (simulation: Simulation | null) => void;
  setPressureState: (pressureState: PressureState | null) => void;
  setMessages: (messages: SimulationMessage[]) => void;
  setWorkspaceArtifacts: (artifacts: SimulationWorkspaceArtifact[]) => void;
  setRevisionsByArtifactId: (
    revisionsByArtifactId: Record<string, SimulationWorkspaceRevision[]>,
  ) => void;
  setInterventions: (interventions: SimulationIntervention[]) => void;
  setAgents: (agents: SimulationAgentState[]) => void;
  setTicks: (ticks: SimulationTickSnapshot[]) => void;
  setSelectedArtifactId: (artifactId: string | null) => void;
  setSelectedAgentId: (agentId: string | null) => void;
  setActiveWorkspaceTab: (tab: WorkspaceTab) => void;
  setActiveDirectorView: (view: DirectorView) => void;
  setDirectorDockExpanded: (value: boolean) => void;
  toggleDirectorDockExpanded: () => void;
  setViewingTick: (tick: number | null) => void;
  setBusy: (value: boolean) => void;
  setError: (value: string | null) => void;
  resetActiveSimulation: () => void;
}

export const isActionableSimulationStatus = (status: SimulationStatus): boolean =>
  status === 'configuring' || status === 'running' || status === 'paused';

export const isHistoricalSimulationStatus = (status: SimulationStatus): boolean =>
  status === 'completed' || status === 'failed' || status === 'cancelled';

const selectPreferredSimulationId = (
  simulations: Simulation[],
  selectedSimulationId: string | null,
): string | null => {
  if (
    selectedSimulationId &&
    simulations.some((simulation) => simulation.id === selectedSimulationId)
  ) {
    return selectedSimulationId;
  }

  return (
    simulations.find((simulation) => isActionableSimulationStatus(simulation.status))?.id ?? null
  );
};

export const useSimulationStore = create<SimulationStoreState>((set) => ({
  simulations: [],
  activeSimulationId: null,
  activeSimulation: null,
  pressureState: null,
  messages: [],
  workspaceArtifacts: [],
  revisionsByArtifactId: {},
  interventions: [],
  agents: [],
  ticks: [],
  selectedArtifactId: null,
  selectedAgentId: null,
  activeWorkspaceTab: 'documents',
  activeDirectorView: 'workspace',
  isDirectorDockExpanded: false,
  viewingTick: null,
  isBusy: false,
  error: null,
  setSimulations: (simulations) =>
    set((state) => ({
      simulations,
      activeSimulationId: selectPreferredSimulationId(simulations, state.activeSimulationId),
    })),
  setActiveSimulationId: (activeSimulationId) => set(() => ({ activeSimulationId })),
  setActiveSimulation: (activeSimulation) => set(() => ({ activeSimulation })),
  setPressureState: (pressureState) => set(() => ({ pressureState })),
  setMessages: (messages) => set(() => ({ messages })),
  setWorkspaceArtifacts: (workspaceArtifacts) =>
    set((state) => ({
      workspaceArtifacts,
      selectedArtifactId:
        state.selectedArtifactId &&
        workspaceArtifacts.some((artifact) => artifact.id === state.selectedArtifactId)
          ? state.selectedArtifactId
          : (workspaceArtifacts[0]?.id ?? null),
    })),
  setRevisionsByArtifactId: (revisionsByArtifactId) => set(() => ({ revisionsByArtifactId })),
  setInterventions: (interventions) => set(() => ({ interventions })),
  setAgents: (agents) =>
    set((state) => ({
      agents,
      selectedAgentId:
        state.selectedAgentId && agents.some((agent) => agent.npcId === state.selectedAgentId)
          ? state.selectedAgentId
          : (agents[0]?.npcId ?? null),
    })),
  setTicks: (ticks) => set(() => ({ ticks })),
  setSelectedArtifactId: (selectedArtifactId) => set(() => ({ selectedArtifactId })),
  setSelectedAgentId: (selectedAgentId) => set(() => ({ selectedAgentId })),
  setActiveWorkspaceTab: (activeWorkspaceTab) => set(() => ({ activeWorkspaceTab })),
  setActiveDirectorView: (activeDirectorView) => set(() => ({ activeDirectorView })),
  setDirectorDockExpanded: (isDirectorDockExpanded) => set(() => ({ isDirectorDockExpanded })),
  toggleDirectorDockExpanded: () =>
    set((state) => ({ isDirectorDockExpanded: !state.isDirectorDockExpanded })),
  setViewingTick: (viewingTick) => set(() => ({ viewingTick })),
  setBusy: (isBusy) => set(() => ({ isBusy })),
  setError: (error) => set(() => ({ error })),
  resetActiveSimulation: () =>
    set(() => ({
      activeSimulationId: null,
      activeSimulation: null,
      pressureState: null,
      messages: [],
      workspaceArtifacts: [],
      revisionsByArtifactId: {},
      interventions: [],
      agents: [],
      ticks: [],
      selectedArtifactId: null,
      selectedAgentId: null,
      activeWorkspaceTab: 'documents',
      activeDirectorView: 'workspace',
      isDirectorDockExpanded: false,
      viewingTick: null,
    })),
}));

export const selectVisibleMessages = (state: SimulationStoreState): SimulationMessage[] => {
  if (state.viewingTick == null) {
    return state.messages;
  }
  const { viewingTick } = state;

  return state.messages.filter((message) => message.tick <= viewingTick);
};

export const selectActionableSimulations = (
  state: Pick<SimulationStoreState, 'simulations'>,
): Simulation[] =>
  state.simulations.filter((simulation) => isActionableSimulationStatus(simulation.status));

export const selectHistoricalSimulations = (
  state: Pick<SimulationStoreState, 'simulations'>,
): Simulation[] =>
  state.simulations.filter((simulation) => isHistoricalSimulationStatus(simulation.status));

export const selectVisibleAgents = (
  state: SimulationStoreState,
): Array<SimulationAgentState | SimulationAgentStateSnapshot> => {
  const viewingTick = state.viewingTick;
  const visibleAgents =
    viewingTick == null
      ? state.agents
      : ([...state.ticks].reverse().find((tick) => tick.tickNumber <= viewingTick)
          ?.agentSnapshots ?? state.agents);
  const castRoleByNpcId = new Map(
    (state.activeSimulation?.cast ?? []).map(
      (member) => [member.npcId, member.role ?? null] as const,
    ),
  );

  return visibleAgents.map((agent) => ({
    ...agent,
    role: agent.role ?? castRoleByNpcId.get(agent.npcId) ?? null,
  }));
};

export const selectVisibleWorkspaceArtifacts = (
  state: SimulationStoreState,
): SimulationWorkspaceArtifact[] => {
  if (state.viewingTick == null) {
    return state.workspaceArtifacts;
  }
  const { viewingTick } = state;

  return state.workspaceArtifacts
    .map((artifact) => {
      if (artifact.createdAtTick > viewingTick) {
        return null;
      }
      if (artifact.type === 'artifact') {
        return artifact;
      }

      const revisions = state.revisionsByArtifactId[artifact.id] ?? [];
      const visibleRevision = revisions
        .slice()
        .sort((left, right) => right.tick - left.tick)
        .find((revision) => revision.tick <= viewingTick);
      if (!visibleRevision) {
        return null;
      }

      if (artifact.type === 'document' && 'content' in visibleRevision) {
        return {
          ...artifact,
          content: visibleRevision.content,
          summary: visibleRevision.content.trim().slice(0, 260),
          status: visibleRevision.status,
          version: visibleRevision.version,
          wordCount: visibleRevision.content.trim().split(/\s+/).filter(Boolean).length,
          updatedAtTick: visibleRevision.tick,
        };
      }

      if (artifact.type === 'plan' && 'tasks' in visibleRevision) {
        const completedTasks = visibleRevision.tasks.filter(
          (task) => task.status === 'completed',
        ).length;
        return {
          ...artifact,
          description: visibleRevision.description,
          tasks: visibleRevision.tasks,
          status: visibleRevision.status,
          version: visibleRevision.version,
          completedTasks,
          totalTasks: visibleRevision.tasks.length,
          updatedAtTick: visibleRevision.tick,
        } satisfies SimulationPlan;
      }

      return artifact;
    })
    .filter((artifact): artifact is SimulationWorkspaceArtifact => artifact !== null)
    .sort((left, right) => right.updatedAtTick - left.updatedAtTick);
};

export const selectSelectedArtifact = (
  state: SimulationStoreState,
): SimulationWorkspaceArtifact | null => {
  const visibleArtifacts = selectVisibleWorkspaceArtifacts(state);
  return (
    visibleArtifacts.find((artifact) => artifact.id === state.selectedArtifactId) ??
    visibleArtifacts[0] ??
    null
  );
};

export const selectSelectedAgent = (
  state: SimulationStoreState,
): SimulationAgentState | SimulationAgentStateSnapshot | null => {
  const visibleAgents = selectVisibleAgents(state);
  return (
    visibleAgents.find((agent) => agent.npcId === state.selectedAgentId) ?? visibleAgents[0] ?? null
  );
};
